import { ArrowLeft, Globe, MapPinOff, Wifi } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { NewAdvancedSearch } from "components/NewAdvancedSearch";
import { ToggleProximityRingsButton } from "components/ProximityRings/ToggleProximityRingsButton";
import useCaseMapStore, { LOCATION_SOURCES } from "stores/useCaseMapStore";
import { CaseMapBulkBar } from "./CaseMapBulkBar";
import { ToggleEventsButton } from "./ToggleEventsButton";
import { ToggleLegendButton } from "./ToggleLegendButton";

export function CaseMapToolbar({
  caseRouteId,
  caseName,
  filters,
  queryParams,
  onApplyFilters,
  pinnedCount,
  notOnMapCount,
  truncatedTotal,
  events,
  proximityRingsAvailable,
}) {
  const locationSource = useCaseMapStore(s => s.locationSource);
  const setLocationSource = useCaseMapStore(s => s.setLocationSource);
  const eventOverlayIds = useCaseMapStore(s => s.eventOverlayIds);
  const showEventsFor = useCaseMapStore(s => s.showEventsFor);
  const dismissEvents = useCaseMapStore(s => s.dismissEvents);
  const checkedMachineIds = useCaseMapStore(s => s.checkedMachineIds);
  const selectedMachineId = useCaseMapStore(s => s.selectedMachineId);
  const legendVisible = useCaseMapStore(s => s.legendVisible);
  const toggleLegend = useCaseMapStore(s => s.toggleLegend);
  const proximityRingsVisible = useCaseMapStore(s => s.proximityRingsVisible);
  const toggleProximityRings = useCaseMapStore(s => s.toggleProximityRings);
  const navigate = useNavigate();

  const isShowingEvents = !!eventOverlayIds?.length;

  // A checked group wins over the single focused machine, so one control covers
  // both "this machine's events" and "the selection's events".
  const eventTargetIds = useMemo(
    () =>
      checkedMachineIds.length
        ? checkedMachineIds
        : selectedMachineId
          ? [selectedMachineId]
          : [],
    [checkedMachineIds, selectedMachineId]
  );

  // Checking more machines while the overlay is up should widen it, not force
  // an off-then-on the analyst has to discover. The pin summary's own toggle
  // already re-targets, so this keeps the two controls behaving alike.
  const willRetarget =
    isShowingEvents &&
    eventTargetIds.length > 0 &&
    !(
      eventOverlayIds.length === eventTargetIds.length &&
      eventTargetIds.every(id => eventOverlayIds.includes(id))
    );

  const handleToggleEvents = useCallback(() => {
    if (isShowingEvents && !willRetarget) {
      dismissEvents();
    } else {
      showEventsFor(eventTargetIds);
    }
  }, [
    isShowingEvents,
    willRetarget,
    dismissEvents,
    showEventsFor,
    eventTargetIds,
  ]);

  // The map is reached from the cases table, the machines table and the case
  // sidebar, so going back beats a fixed destination — it returns the analyst to
  // the table scroll or the case tab they came from. A pasted or bookmarked link
  // has nothing behind it (React Router stamps entry 0 on a fresh session), so
  // that case falls back to the case the map belongs to.
  const handleBack = useCallback(() => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/cases/${caseRouteId}`, { replace: true });
    }
  }, [navigate, caseRouteId]);

  return (
    <div className="tw:flex tw:justify-between tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-(--color-border) tw:bg-(--color-surface) tw:px-4 tw:py-2">
      <div className="tw:flex tw:items-center tw:gap-2">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={handleBack}
        >
          <ArrowLeft />
        </Button>
        <h1 className="tw:truncate tw:text-base! tw:font-semibold! tw:[font-family:var(--font-heading)] tw:text-(--color-text-primary) tw:m-0!">
          {caseName ?? "Machines Map"}
        </h1>
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-(--color-text-secondary)">
          <span>{pinnedCount} on map</span>
          {notOnMapCount > 0 && (
            <Badge
              variant="outline"
              className="tw:bg-yellow-50 tw:text-yellow-700 tw:dark:bg-yellow-950 tw:dark:text-yellow-300"
            >
              <MapPinOff className="tw:size-3" /> {notOnMapCount} not on map
            </Badge>
          )}
          {truncatedTotal && (
            <Badge variant="secondary">
              showing first {pinnedCount + notOnMapCount} of {truncatedTotal}
            </Badge>
          )}
        </div>
        <CaseMapBulkBar />
      </div>

      <div className="tw:flex tw:items-center tw:gap-2">
        {isShowingEvents && (
          <Badge variant="secondary">
            {events.isLoading
              ? "Loading events…"
              : events.isTruncated
                ? `${events.plottedCount} of ${events.total} events`
                : `${events.plottedCount} events`}
          </Badge>
        )}
        <ToggleEventsButton
          isShowing={isShowingEvents}
          willRetarget={willRetarget}
          disabled={!isShowingEvents && !eventTargetIds.length}
          onToggle={handleToggleEvents}
        />
        {proximityRingsAvailable && (
          <ToggleProximityRingsButton
            isShowing={proximityRingsVisible}
            onToggle={toggleProximityRings}
          />
        )}
        <ToggleLegendButton isShowing={legendVisible} onToggle={toggleLegend} />
        <Tabs value={locationSource} onValueChange={setLocationSource}>
          <TabsList aria-label="Location source">
            <TabsTrigger value={LOCATION_SOURCES.WIFI}>
              <Wifi data-icon="inline-start" />
              WiFi
            </TabsTrigger>
            <TabsTrigger value={LOCATION_SOURCES.IP}>
              <Globe data-icon="inline-start" />
              IP
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <NewAdvancedSearch
          filters={filters}
          queryParams={queryParams}
          tableName="machines"
          onApply={onApplyFilters}
        />
      </div>
    </div>
  );
}
